import { isEqual, pickBy } from 'es-toolkit';

/**
 * Removes all falsey values from an object.
 * @param obj @type {TObj} The object to remove empty values from.
 * @returns @type {Partial<TObj>} The object with empty values removed.
 * @example
 * const obj = {
 *   a: 1,
 *   b: null,
 *   c: undefined,
 *   d: "",
 *   e: "hello",
 *   f: 0,
 *   g: false,
 *   h: true,
 *   i: [],
 *   j: {},
 *   k: [1, 2, 3],
 *   l: { a: 1, b: 2, c: 3 },
 *   m: { a: 1, b: null, c: undefined, d: "", e: "hello", f: 0, g: false, h: true, i: [], j: {}, k: [1, 2, 3], l: { a: 1, b: 2, c: 3 } },
 * };
 * const result = removeFalsey(obj);
 * console.log(result);
 * // {
 * //   a: 1,
 * //   e: "hello",
 * //   h: true,
 * //   i: [],
 * //   j: {},
 * //   k: [1, 2, 3],
 * //   l: { a: 1, b: 2, c: 3 },
 * //   m: { a: 1, b: null, c: undefined, d: "", e: "hello", f: 0, g: false, h: true, i: [], j: {}, k: [1, 2, 3], l: { a: 1, b: 2, c: 3 } },
 * // }
 */
export function removeFalsey<TObj extends object>(obj: TObj): Partial<TObj> {
  // @ts-expect-error - The following is okay for such a small util function. Ideall, we kill this stupid util function at some point
  const result: Partial<TObj> = Object.fromEntries(Object.entries(obj).filter(([, v]) => !!v));
  return result;
}

/**
 * Returns the difference between two objects.
 * @param obj1 @type {T1} The first object.
 * @param obj2 @type {T2} The second object.
 * @returns @type {Partial<T1>} The difference between the two objects.
 */
export function getObjectDiff<T1 extends object>(obj1: T1, obj2: Record<string, unknown>) {
  return pickBy(obj1, (v, k) => !isEqual(obj2[String(k)], v));
}

/**
 * Type-safe variant of removeFalsey that validates input against an expected target type.
 * Use this when building GraphQL mutation inputs to catch field name mismatches at compile time.
 *
 * Unlike removeFalsey, this function requires an explicit type parameter representing
 * the expected output type. If you pass an object with properties not in the target type,
 * TypeScript will report a compile-time error.
 *
 * @typeParam TTarget - The expected output type (e.g., a GraphQL mutation input type)
 * @param obj - The object to remove falsey values from, constrained to TTarget's properties
 * @returns The object with falsey values removed
 *
 * @example
 * type MutationInput = { title?: string; description?: string };
 *
 * // This compiles - valid fields
 * removeFalseyStrict<MutationInput>({ title: "test" });
 *
 * // This fails to compile - 'subject' is not in MutationInput
 * removeFalseyStrict<MutationInput>({ subject: "test" }); // Error!
 */
export function removeFalseyStrict<TTarget extends object>(obj: Partial<TTarget>): Partial<TTarget> {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Object.fromEntries returns object, truthy filter is intentional
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => !!v)) as Partial<TTarget>;
}
